<template>
  <v-dialog v-model="dialog" max-width="1100" scrollable>
    <v-card class="shadow">
      <v-card-title>
        <v-row justify="space-between" align="end">
          <v-col cols="12" md="7" xl="8">
            <div class="grey--text mb-1" style="font-size: 13px">
              Otso Panalo Game
            </div>
            <div class="text-h6 font-weight-medium">Game Details</div>
            <div class="grey--text text--darken-1" style="font-size: 13px">
              Update the linked ending game, betting deadline, and prize
              allocation.
            </div>
          </v-col>

          <v-col cols="12" md="5" xl="4" class="text-md-right">
            <v-chip small :class="bettingChipClass" class="mr-2 mb-2">
              Betting {{ bettingEnabled ? "Enabled" : "Disabled" }}
            </v-chip>
            <v-chip small :class="statusChipClass" class="mr-2 mb-2">
              {{ statusLabel }}
            </v-chip>

            <v-btn text color="secondary" class="mb-2" @click="closeDialog">
              <v-icon left>mdi-close</v-icon>
              Close
            </v-btn>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="secondary"
                  class="mb-2"
                  v-bind="attrs"
                  v-on="on"
                  @click="loadDetails"
                  :disabled="loading"
                >
                  <v-icon>mdi-cached</v-icon>
                </v-btn>
              </template>
              <span>Refresh</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4 pa-md-6">
        <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <v-alert v-if="isStarted" type="warning" dense outlined class="mb-4">
          This game has already started. All fields are now locked.
        </v-alert>

        <v-alert v-else type="info" dense outlined class="mb-4">
          Review the current setup below, then save the updated game
          configuration.
        </v-alert>

        <v-alert
          v-if="!loadingGameConfig && !gameConfig"
          type="warning"
          dense
          outlined
          class="mb-4"
        >
          No active Otso Panalo game config is available. Activate a game config
          first before updating this game.
        </v-alert>

        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col cols="12" lg="5">
              <v-sheet class="pa-4 grey lighten-5 rounded fill-height">
                <div class="text-caption grey--text text--darken-1 mb-1">
                  Selected Ending Game
                </div>

                <template v-if="activeGame">
                  <div class="font-weight-medium mb-1">
                    {{ activeGame.league || "-" }}
                  </div>
                  <div class="mb-3">
                    {{ activeGame.team_a_name }} {{ activeGame.team_a_alias }}
                    <span class="text-overline my-2 mx-2 font-italic">vs</span>
                    {{ activeGame.team_b_name }} {{ activeGame.team_b_alias }}
                  </div>

                  <div class="mb-3">
                    <v-chip
                      small
                      class="mr-2 mb-2 blue lighten-5 blue--text text--darken-2"
                    >
                      Game ID {{ id }}
                    </v-chip>
                    <v-chip small :class="statusChipClass" class="mb-2">
                      {{ statusLabel }}
                    </v-chip>
                  </div>

                  <div class="text-caption grey--text text--darken-1">
                    Schedule
                  </div>
                  <div class="mb-3">
                    {{
                      activeGame.schedule
                        ? formatDate(activeGame.schedule, true)
                        : "-"
                    }}
                  </div>
                </template>

                <div v-else class="grey--text text--darken-1">
                  Game details will appear here after loading.
                </div>
              </v-sheet>
            </v-col>

            <v-col cols="12" lg="7">
              <v-row>
                <v-col cols="12">
                  <div class="text-overline mb-2">Betting Window</div>
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="bettingDeadlineDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.betting_deadline_date"
                        :rules="[rules.required]"
                        label="Betting Deadline Date"
                        readonly
                        outlined
                        dense
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        :disabled="isFormLocked"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.betting_deadline_date"
                      :readonly="isFormLocked"
                      @input="bettingDeadlineDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    ref="bettingDeadlineTimeMenu"
                    v-model="bettingDeadlineTimeMenu"
                    :close-on-content-click="false"
                    :return-value.sync="form.betting_deadline_time"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.betting_deadline_time"
                        :rules="[rules.required]"
                        label="Betting Deadline Time"
                        readonly
                        outlined
                        dense
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        :disabled="isFormLocked"
                      ></v-text-field>
                    </template>
                    <v-time-picker
                      v-if="bettingDeadlineTimeMenu"
                      v-model="form.betting_deadline_time"
                      :readonly="isFormLocked"
                      @click:minute="
                        $refs.bettingDeadlineTimeMenu.save(
                          form.betting_deadline_time,
                        )
                      "
                    ></v-time-picker>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    :value="statusLabel"
                    label="Current Status"
                    readonly
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    :value="bettingStatusLabel"
                    label="Current Betting"
                    readonly
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12">
              <div class="text-overline mb-2">Prize Configuration</div>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayBetAmount"
                label="Bet Amount"
                prefix="PHP"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="displayPoolPercentage"
                label="Pool Percentage"
                suffix="%"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayJackpotPrize"
                label="Jackpot Prize"
                prefix="PHP"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="displaySecondPrize"
                label="Second Prize"
                prefix="PHP"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                :value="displayThirdPrize"
                label="Third Prize"
                prefix="PHP"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="displayFourthPrize"
                label="Fourth Prize"
                prefix="PHP"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row dense class="mt-4">
            <v-col cols="12" md="6">
              <v-btn
                block
                color="primary"
                type="submit"
                :disabled="!valid || isFormLocked || !gameConfig"
                :loading="submitLoading"
              >
                Update Game
              </v-btn>
            </v-col>
            <v-col cols="12" md="6">
              <v-btn
                block
                outlined
                color="black"
                :disabled="submitLoading"
                @click="goBack"
              >
                Back
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import formRules from "@/helpers/formRules";
import formatDate from "@/helpers/formatDate";
import responseGet from "@/helpers/api_request_get";
import responsePost from "@/helpers/api_request_post";

export default {
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data: () => ({
    dialog: true,
    valid: true,
    rules: formRules,
    loading: false,
    loadingGameConfig: false,
    submitLoading: false,
    errorMessage: "",
    availableGames: [],
    details: null,
    gameConfig: null,
    bettingDeadlineDateMenu: false,
    bettingDeadlineTimeMenu: false,
    form: {
      ending_game_id: "",
      betting_deadline_date: "",
      betting_deadline_time: "",
      bet_amount: "0.00",
      jackpot_prize: "0.00",
      second_prize: "0.00",
      third_prize: "0.00",
      fourth_prize: "0.00",
      pool_percentage: "0.00",
    },
  }),

  computed: {
    selectedGame() {
      return (
        this.availableGames.find(
          (game) =>
            String(game.ending_game_id) === String(this.form.ending_game_id),
        ) || null
      );
    },
    activeGame() {
      return this.selectedGame || this.details || null;
    },
    isStarted() {
      if (!this.details) {
        return false;
      }

      return (
        this.details.betting === "Enabled" ||
        Number(this.details.game_status_id) === 2
      );
    },
    isFormLocked() {
      return this.loading || this.submitLoading || this.isStarted;
    },
    statusLabel() {
      if (!this.details || !this.details.game_status) {
        return "Not yet started";
      }

      return this.details.game_status;
    },
    statusChipClass() {
      return this.statusLabel === "Not yet started"
        ? "orange lighten-5 orange--text"
        : "green lighten-5 green--text";
    },
    bettingStatusLabel() {
      if (!this.details || !this.details.betting) {
        return "Disabled";
      }

      return this.details.betting;
    },
    bettingEnabled() {
      return this.bettingStatusLabel === "Enabled";
    },
    bettingChipClass() {
      return this.bettingStatusLabel === "Enabled"
        ? "green lighten-5 green--text"
        : "orange lighten-5 orange--text";
    },
    displayBetAmount() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.bet_amount)
        : "";
    },
    displayPoolPercentage() {
      return this.gameConfig
        ? this.formatPercent(this.gameConfig.pool_percentage)
        : "";
    },
    displayJackpotPrize() {
      return this.gameConfig
        ? this.formatMoney(
            this.gameConfig.effective_jackpot_prize ||
              this.gameConfig.jackpot_prize,
          )
        : "";
    },
    displaySecondPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.second_prize)
        : "";
    },
    displayThirdPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.third_prize)
        : "";
    },
    displayFourthPrize() {
      return this.gameConfig
        ? this.formatMoney(this.gameConfig.fourth_prize)
        : "";
    },
    bettingDeadlineLabel() {
      if (
        !this.form.betting_deadline_date ||
        !this.form.betting_deadline_time
      ) {
        return "-";
      }

      return `${this.form.betting_deadline_date} ${this.form.betting_deadline_time}`;
    },
  },

  created() {
    this.loadDetails();
  },

  watch: {
    dialog(value) {
      if (!value) {
        this.closeDialog();
      }
    },
    id() {
      this.loadDetails();
    },
  },

  methods: {
    formatDate,
    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const amount = Number(value);

      if (Number.isNaN(amount)) {
        return value;
      }

      return amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPercent(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const amount = Number(value);

      if (Number.isNaN(amount)) {
        return value;
      }

      return amount.toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    closeDialog() {
      if (!this.dialog) {
        this.$emit("close");
        if (this.$route.name === "OtsoPanaloEditGame") {
          this.$router.push({ name: "OtsoPanaloGames" });
        }
        return;
      }

      this.dialog = false;
    },
    async loadDetails() {
      try {
        this.loading = true;
        this.loadingGameConfig = true;
        this.errorMessage = "";

        const [detailsResponse, availableResponse, configResponse] =
          await Promise.all([
            responseGet("", `/otso_panalo/games/details/${this.id}`),
            responseGet("", "/otso_panalo/games/available_ending_games"),
            responseGet("", "/otso_panalo/game_configs/active"),
          ]);

        if (detailsResponse.status === 200) {
          this.details = detailsResponse.data.details;
          this.form.ending_game_id = this.details.ending_game_id;
          this.form.betting_deadline_date = this.details.betting_deadline
            ? this.details.betting_deadline.slice(0, 10)
            : "";
          this.form.betting_deadline_time = this.details.betting_deadline
            ? this.details.betting_deadline.slice(11, 16)
            : "";
          this.form.bet_amount = this.details.bet_amount;
          this.form.jackpot_prize = this.details.jackpot_prize;
          this.form.second_prize = this.details.second_prize;
          this.form.third_prize = this.details.third_prize;
          this.form.fourth_prize = this.details.fourth_prize;
          this.form.pool_percentage = this.details.pool_percentage;
        } else if (detailsResponse.status === 401) {
          this.$store.commit("setError", "Unauthorized");
          return;
        } else {
          this.errorMessage = "Failed to load Otso Panalo game details.";
          return;
        }

        if (availableResponse.status === 200) {
          this.availableGames = (availableResponse.data.records || []).map(
            (game) => ({
              ...game,
              display_name: `${game.league} | ${game.team_a_name} ${
                game.team_a_alias
              } vs ${game.team_b_name} ${game.team_b_alias} | ${formatDate(
                game.schedule,
                true,
              )}`,
            }),
          );
        }

        if (configResponse.status === 200) {
          this.gameConfig = configResponse.data.details || null;
        } else if (configResponse.status === 401) {
          this.$store.commit("setError", "Unauthorized");
          return;
        } else {
          this.gameConfig = null;
        }

        if (this.details) {
          const currentGameOption = {
            ending_game_id: this.details.ending_game_id,
            league: this.details.league,
            team_a_name: this.details.team_a_name,
            team_a_alias: this.details.team_a_alias,
            team_b_name: this.details.team_b_name,
            team_b_alias: this.details.team_b_alias,
            schedule: this.details.schedule,
            display_name: `${this.details.league} | ${
              this.details.team_a_name
            } ${this.details.team_a_alias} vs ${this.details.team_b_name} ${
              this.details.team_b_alias
            } | ${formatDate(this.details.schedule, true)}`,
          };

          if (
            !this.availableGames.find(
              (game) =>
                game.ending_game_id === currentGameOption.ending_game_id,
            )
          ) {
            this.availableGames = [currentGameOption, ...this.availableGames];
          }
        }
      } catch (error) {
        this.gameConfig = null;
        this.errorMessage = "Failed to load Otso Panalo game details.";
      } finally {
        this.loading = false;
        this.loadingGameConfig = false;
      }
    },
    getMessage(payload, fallback) {
      if (!payload) {
        return fallback;
      }
      if (payload.messages) {
        const firstKey = Object.keys(payload.messages)[0];
        if (firstKey && typeof payload.messages[firstKey] === "string") {
          return payload.messages[firstKey];
        }
      }
      if (typeof payload.message === "string") {
        return payload.message;
      }
      return fallback;
    },
    async handleSubmit() {
      if (!this.$refs.form.validate() || this.submitLoading || this.isStarted) {
        return;
      }

      if (!this.gameConfig) {
        this.errorMessage =
          "No active Otso Panalo game config is available for this game.";
        return;
      }

      try {
        this.submitLoading = true;
        this.errorMessage = "";

        const formData = new FormData();
        formData.append("id", this.id);
        formData.append("ending_game_id", this.form.ending_game_id);
        formData.append(
          "betting_deadline_date",
          this.form.betting_deadline_date,
        );
        formData.append(
          "betting_deadline_time",
          this.form.betting_deadline_time,
        );

        const response = await responsePost(
          formData,
          "otso_panalo/games/update",
        );

        if (response.status === 200) {
          this.$emit("updated");

          if (this.$route.name === "OtsoPanaloEditGame") {
            this.$router.push({
              name: "OtsoPanaloGames",
              query: { updated: "1" },
            });
          }
        } else if (response.status === 401) {
          this.$store.commit("setError", "Unauthorized");
        } else {
          this.errorMessage = this.getMessage(
            response.data,
            "Failed to update Otso Panalo game.",
          );
        }
      } catch (error) {
        this.errorMessage = "Failed to update Otso Panalo game.";
      } finally {
        this.submitLoading = false;
      }
    },
    goBack() {
      this.closeDialog();
    },
  },
};
</script>
